import { AfterContentInit, Directive, ElementRef, HostListener, Input, OnChanges, QueryList, SimpleChanges } from '@angular/core';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';


@Directive({
    selector: '[agprListKeyboardNavigation]',
    standalone: false
})
export class ListKeyboardNavigationDirective implements AfterContentInit, OnChanges {
    // used to disable the directive within it, as angular does not allow applying directive conditionally
    @Input() listKeyboardNavigationDirectiveEnabled: boolean = true;
    @Input() listOrientation: string = 'vertical';
    // set this to true if the list item has interactive elements in it (button, checkbox, etc.)
    @Input() focusOnChild: boolean = false;
    // exclude other list items from tab order
    @Input() manageTabIndex: boolean = true;
    @Input() wrap: boolean = true;
    @Input() initialFocusIndex: number = 0;
    // used to trigger change detection for updating tabindex when the list itself is changed
    @Input() list: any[] = [];
    // used in virtual scrolling to reset tabindex
    @Input() enableNavigationCounter: boolean = false;
    @Input() set focusableElementCustomSelector(selector: string) {
        this.focusableElementSelectors += ', ' + selector;
        this.setTabindex();
    }

    listItems: HTMLElement[];
    navigationCounter: number = 0;

    focusableElementSelectors: string = 'button, [href], input, select, textarea, [tabindex]';
    // used to skip list items that are not interactive, e.g. social filter lhp where filter group header is a list item
    // but it should not be focusable
    readonly unfocusableListItemClassName: string = 'agpr-unfocusableListItem';
    readonly maxKeyStrokesBeforeUpdate: number = 20;

    constructor(
        private el: ElementRef,
        private localizationService: LocalizationService
    ) {
    }

    ngAfterContentInit(): void {
        if (!this.listKeyboardNavigationDirectiveEnabled) {
            return;
        }
        this.setTabindex();
        const description = document.createElement('SPAN');
        description.textContent = this.localizationService.transform('Core.listNavigation');
        description.classList.add('u-srOnly');
        description.id = this.el.nativeElement.id + '_keyboardNavigationDescription';
        this.el.nativeElement.after(description);
        let existingValue = this.el.nativeElement.getAttribute('aria-describedby');
        if (!!existingValue) {
            existingValue += ' ' + description.id;
            this.el.nativeElement.setAttribute('aria-describedby', existingValue);
        } else {
            this.el.nativeElement.setAttribute('aria-describedby', description.id);
        }

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.listKeyboardNavigationDirectiveEnabled) {
            return;
        }
        this.setTabindex();
    }

    @HostListener('keydown', ['$event']) onKeyDown(e: KeyboardEvent) {
        if (!this.listKeyboardNavigationDirectiveEnabled) {
            return;
        }
        if (e.key === 'Home') {
            // use 'Home' key to return the focus to the first item in the list
            this.returnToTop();
        }
        // Allows keyboard navigation for radio buttons inside list elements
        if ((e.target as HTMLElement).nodeName === 'INPUT' && (e.target as HTMLElement).getAttribute('type') === 'radio') {
            return;
        }
        if (
            (this.listOrientation === 'vertical' && e.key !== 'ArrowDown' && e.key !== 'ArrowUp')
            || (this.listOrientation === 'horizontal' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')
        ) {
            return;
        }
        if (this.el.nativeElement.contains(document.activeElement)) {
            let activeListItemElement = document.activeElement;
            while (activeListItemElement.nodeName !== 'LI') {
                activeListItemElement = activeListItemElement.parentElement;
            }
            const focusedElementIndex = [...this.el.nativeElement.children].indexOf(activeListItemElement);
            if (focusedElementIndex > -1) {
                this.handleFocusChange(e, focusedElementIndex);
            }
        }
    }

    private setTabindex(): void {
        requestAnimationFrame(() => {
            this.listItems = Array.from(this.el.nativeElement.children);
            if (!this.manageTabIndex) {
                return;
            }
            if (this.listItems.length > 0) {
                const initialItem = this.listItems[this.initialFocusIndex];
                const startIndex = initialItem && !initialItem.classList.contains(this.unfocusableListItemClassName) ? this.initialFocusIndex : 0;
                let initialFocusSet = false;
                for (let i = 0; i < this.listItems.length; i++) {
                    // const listItem = this.el.nativeElement.children[i];
                    this.el.nativeElement.children[i].setAttribute('tabindex', '-1');
                    this.setChildrenTabIndexes(this.el.nativeElement.children[i], '-1');
                    if (!initialFocusSet && i >= startIndex && !this.el.nativeElement.children[i].classList?.contains(this.unfocusableListItemClassName)) {
                        if (!this.focusOnChild) {
                            this.el.nativeElement.children[i].setAttribute('tabindex', '0');
                        }
                        this.setChildrenTabIndexes(this.el.nativeElement.children[i], '0');
                        initialFocusSet = true;
                    }
                }
            }
        });
    }

    private returnToTop(): void {
        if (this.manageTabIndex) {
            document.activeElement.setAttribute('tabindex', '-1');
        }
        this.setNextFocusableElement(0);
    }

    /**
     * This method should manage the focus for all interactive elements
     * We should make sure that at one time only the current item has tabindex = 0 while others = -1
     * In this way, when the users tab again, they should be able to skip the list
     * This won't work for list items that have multiple interactive elements in it, because the tab purpose is undefined
     */
    private handleFocusChange(e: KeyboardEvent, currentIndex: number): void {
        let nextIndex;
        if (
            (this.listOrientation === 'vertical' && e.key === 'ArrowDown')
            || (this.listOrientation === 'horizontal' && e.key === 'ArrowRight')
        ) {
            if (currentIndex < this.listItems?.length - 1) {
                nextIndex = currentIndex + 1;
                e.stopPropagation();
            } else {
                if (this.wrap) {
                    nextIndex = 0;
                    e.stopPropagation();
                } else {
                    return;
                }
            }
            while (this.listItems[nextIndex]?.classList.contains(this.unfocusableListItemClassName)) {
                if (nextIndex < this.listItems.length - 1) {
                    nextIndex++;
                } else if (this.wrap) {
                    nextIndex = 0;
                }
            }
        } else if (
            (this.listOrientation === 'vertical' && e.key === 'ArrowUp')
            || (this.listOrientation === 'horizontal' && e.key === 'ArrowLeft')
        ) {
            nextIndex = currentIndex > 0 ? currentIndex - 1 :
                (this.wrap ? this.listItems.length - 1 : currentIndex);
            if (currentIndex > 0) {
                nextIndex = currentIndex - 1;
                e.stopPropagation();
            } else {
                if (this.wrap) {
                    nextIndex = this.listItems.length - 1;
                    e.stopPropagation();
                } else {
                    return;
                }
            }
            while (this.listItems[nextIndex]?.classList.contains(this.unfocusableListItemClassName)) {
                if (nextIndex > 0) {
                    nextIndex--;
                } else if (this.wrap) {
                    nextIndex = this.listItems.length - 1;
                }
            }
        }
        if (this.manageTabIndex) {
            // set the old element tabindex back to -1 so it is not in tab order
            this.el.nativeElement.children[currentIndex]?.setAttribute('tabindex', '-1');
            this.setChildrenTabIndexes(this.el.nativeElement.children[currentIndex], '-1');
        }
        this.setNextFocusableElement(nextIndex);
        e.preventDefault();
    }

    private findFirstFocusableChild(element: HTMLElement): HTMLElement {
        const focusableChildElements: HTMLElement[] = Array.from(element.querySelectorAll(this.focusableElementSelectors));

        return  focusableChildElements.length > 0 ? focusableChildElements[0] : null;
    }

    private setNextFocusableElement(index: number): void {
        if (this.manageTabIndex) {
            if (!this.focusOnChild) {
                this.el.nativeElement.children[index]?.setAttribute('tabindex', '0');
            }
            this.setChildrenTabIndexes(this.el.nativeElement.children[index], '0');
        }
        if (this.focusOnChild) {
            const firstFocusableChild = this.findFirstFocusableChild(this.el.nativeElement.children[index]);
            firstFocusableChild?.focus();
        } else {
            this.el.nativeElement.children[index]?.focus();
        }
        // virtual scrolling loads list items in batches, so need to update tabindice regularly
        if (this.enableNavigationCounter) {
            this.navigationCounter++;
            if (this.navigationCounter >= this.maxKeyStrokesBeforeUpdate) {
                this.setTabindex();
                this.navigationCounter = 0;
            }
        }
    }

    private setChildrenTabIndexes(element: HTMLElement, tabIndex: string): void {
        element?.querySelectorAll(this.focusableElementSelectors).forEach(elem => {
            elem.setAttribute('tabindex', tabIndex);
        });
    }
}
